// Pure logic for the "Formus Recommended" stem cards: deciding whether the current
// selection matches a recommended stem, and which head to apply when one is clicked.
import { getCatalogComponent } from '@/domain/catalog/catalog';
import { formatNumberSign } from '@/util/format/formatNumberSign';
import type { CatalogHead } from '@/domain/catalog/head';
import type { Url } from '@/domain/types';
import { getSelectableHeadOffsetsForSystem } from './stemHeadOffsets';
import type { SelectableHeadOffset, SelectableStem } from '../stem.types';

export type RecommendedHeadQuery = {
    catalogHeads: Map<string, CatalogHead> | null;
    templateHeadUrl: Url | null;
    selectedCupSize: number | null;
    dualMobility: boolean;
};

const compatibleHeadsFor = (
    stem: SelectableStem,
    query: RecommendedHeadQuery,
): SelectableHeadOffset[] => {
    if (!query.catalogHeads || !query.templateHeadUrl) {
        return [];
    }
    const headSize = getCatalogComponent(query.catalogHeads, query.templateHeadUrl).size;
    return getSelectableHeadOffsetsForSystem({
        heads: query.catalogHeads,
        stemSystem: stem.catalogStem.system,
        headSize,
        selectedCupSize: query.selectedCupSize,
        dualMobility: query.dualMobility,
    });
};

/**
 * Whether the currently selected head matches a recommended stem's head. With a catalog
 * present, matches by offset value (dual-mobility-equivalent heads); otherwise by head URL.
 */
export const isHeadMatchingRecommendedStem = (
    recommendedStem: SelectableStem,
    selectedHead: SelectableHeadOffset | null,
    query: RecommendedHeadQuery,
): boolean => {
    if (!selectedHead) {
        return false;
    }
    if (query.catalogHeads && query.templateHeadUrl) {
        const recommended = formatNumberSign(recommendedStem.catalogHead.offset);
        return (
            selectedHead.text === recommended &&
            compatibleHeadsFor(recommendedStem, query).some((h) => h.text === selectedHead.text)
        );
    }
    return selectedHead.headUrl === recommendedStem.headUrl;
};

/** The head offset to apply when a recommended stem is clicked. */
export const headForRecommendedStem = (
    stem: SelectableStem,
    query: RecommendedHeadQuery,
): SelectableHeadOffset => {
    const targetOffsetText = formatNumberSign(stem.catalogHead.offset);
    const fallback = { text: targetOffsetText, headUrl: stem.headUrl };
    if (!query.catalogHeads || !query.templateHeadUrl) {
        return fallback;
    }
    return compatibleHeadsFor(stem, query).find((h) => h.text === targetOffsetText) ?? fallback;
};